import { removeDuplicates } from 'utils/arrays';
import { IsJsonString } from 'utils/mails';
import _ from 'lodash';
import {
  SmsFemaleConnectedOptions,
  SmsFemaleEPDMConnectionOptions,
  SmsFemaleConnectionOptions,
  SmsMaleConnectionOptions,
  DinFemaleConnectedOptions,
  DinFemaleConnectionOptions,
  DinFemaleEPDMConnectionOptions,
  DinMaleConnectionOptions,
  ClampConnectionOptions,
  CamlockConnectionOptions,
  GuilleminConnectionOptions,
  typeOptions,
} from 'containers/specifications/constants';

const flexiblesCodes = [
  { name: 'alcodial', letter: 'A' },
  { name: 'aliklerg2', letter: 'B' },
  { name: 'alikler d', letter: 'C' },
  { name: 'bioclean', letter: 'D' },
  { name: 'chemiklerupe', letter: 'E' },
  { name: 'citerdial', letter: 'F' },
  { name: 'lactadial', letter: 'G' },
  { name: 'millesime', letter: 'H' },
  { name: 'multidialupe', letter: 'I' },
  { name: 'supervracalec', letter: 'J' },
  { name: 'trellevacalec', letter: 'K' },
  { name: 'trellvin', letter: 'L' },
  { name: 'vendange', letter: 'M' },
  { name: 'vinitrell', letter: 'N' },
];

function filterDryMatter(codes) {
  if (codes.length > 1 && codes.includes('E')) {
    const index = codes.findIndex(el => el === 'E');
    if (index !== -1) {
      codes[index] = 'eWarning';
    }
  }
  return codes;
}

function getCodePerRecipe(ingredients) {
  const codes = [];
  const ingredientsArray = typeof ingredients === 'string' ? JSON.parse(ingredients) : ingredients;
  ingredientsArray.forEach(ingredient => {
    ingredient.code.forEach(code => {
      if (code.letters.code === 'B' && code.letters.condition === 'X(*)') {
        if (ingredient.condition && ingredient.condition < 4.5) {
          codes.push(code.letters.code);
        }
      } else {
        codes.push(code.letters.code);
      }
    });
  });
  const filteredCodes = removeDuplicates(codes);
  const checkedDryMatter = filterDryMatter(filteredCodes);
  return checkedDryMatter.sort();
}

function getCodesWithMessage(ingredients) {
  const codes = getCodePerRecipe(ingredients);
  let hasWarning = false;
  if (codes.includes('eWarning')) {
    hasWarning = true;
    const index = codes.indexOf('eWarning');
    codes.splice(index, 1);
  }
  const message = hasWarning ? 'Le simulant E n’est pas pris en compte '
    + 'car l\'ensemble n\'est pas une denrée alimentaire sèche' : '';
  return { message, codes };
}

function getCodeLetterForPdf(ingredients) {
  const { message, codes } = getCodesWithMessage(ingredients);
  if (!codes) return false;
  return { message, codes: codes.join(', ') };
}

function getCodeForFlexibleFeatures(recipe) {
  const ingredients = IsJsonString(recipe?.ingredients) ? JSON.parse(recipe?.ingredients) : recipe?.ingredients || {};
  const codes = getCodePerRecipe(ingredients);
  return codes;
}

function getSmsConnectionDnByCode() {
  const connection = _.flatten([SmsFemaleConnectedOptions,
    SmsFemaleEPDMConnectionOptions,
    SmsFemaleConnectionOptions,
    SmsMaleConnectionOptions]).find(con => con.value === this);
  // eslint-disable-next-line no-debugger
  return connection?.label;
}

function getDinConnectionDnByCode(code) {
  const connection = _.flatten([DinFemaleConnectedOptions,
    DinFemaleConnectionOptions,
    DinFemaleEPDMConnectionOptions,
    DinMaleConnectionOptions]).find(con => con.value === code);
  return connection?.label;
}

function getClampConnectionDnByCode(code) {
  const connection = ClampConnectionOptions.find(con => con.value === code);
  return connection?.label;
}

function getCamlockConnectionDnByCode(code) {
  const connection = CamlockConnectionOptions.find(con => con.value === code);
  return connection?.label;
}

function getGuilleminConnectionDnByCode(code) {
  const connection = GuilleminConnectionOptions.find(con => con.value === code);
  return connection?.label;
}

function getMaconConnectionDnByCode(code) {
  const connection = typeOptions.find(con => con.doptions[0].value === code);
  return connection?.label;
}

const connectionApplies = {
  sms: getSmsConnectionDnByCode,
  din: getDinConnectionDnByCode,
  clamp: getClampConnectionDnByCode,
  camlock: getCamlockConnectionDnByCode,
  guillemin: getGuilleminConnectionDnByCode,
  macon: getMaconConnectionDnByCode
};

function getConnectionDiameterByCode(type, code) {
  return connectionApplies[type.toLowerCase()].apply(code);
}

function getRTypeDiameter(type, diameter) {
  const options = typeOptions.find(con => con.doptions[0].value === type)?.doptions;
  return options.find(opt => opt.value === diameter)?.label;
}

function getWasherCode(number, color) {
  const letters = 'ABCDEFGHIJKMNOPQRSTUV';
  if (number < 1) {
    return letters.charAt(0);
  }
  if (color === 'yellow') {
    return letters.charAt(number);
  }
  return letters.charAt(number + 10);
}

function getFlexibleCode(name) {
  return flexiblesCodes.find(flexible => flexible.name === name.replace(/\s/g, '').toLowerCase())?.letter;
}

function getFlexibleDiameter(flexible) {
  const dn = flexible?.attributes.find(attribute => attribute.name === 'dn');
  return dn?.options[0];
}

function getFlexibleLengthUnities(flexibleLenght) {
  let thousands = null;
  let unitsOfThousands = null;
  const lengthNumber = flexibleLenght.toLocaleString().split(/\s/g);
  console.log(lengthNumber);
  thousands = lengthNumber[0].length > 1 ? lengthNumber[0].charAt(0) : 0;
  unitsOfThousands = lengthNumber[0].length > 1 ? lengthNumber[0].charAt(1) : lengthNumber[0].charAt(0);
  return {
    thousands,
    unitsOfThousands,
    tensOfCentimeters: lengthNumber[1].slice(0, 2),
    unityOfCentimeters: lengthNumber[1].slice(2),
  };
}

function getProductCode(specification, flexible) {
  const code = 'T5';
  const {
    // eslint-disable-next-line no-unused-vars
    r1Type, r1Diameter, r2Type, r2Diameter, r1asherNumber, r1WasherColor, r2asherNumber, r2WasherColor, length
  } = specification;
  const third = typeOptions.find(con => con.doptions[0].value === r1Type)?.letter;
  const fourth = getRTypeDiameter(r1Type, r1Diameter);
  const fifth = typeOptions.find(con => con.doptions[0].value === r2Type)?.letter;
  const sixth = getRTypeDiameter(r2Type, r2Diameter);
  const seventh = getWasherCode(r1asherNumber, r1WasherColor);
  const eighth = getFlexibleCode(flexible.name);
  const ninth = getFlexibleDiameter(flexible);
  const {
    tenth, eleventh, twelfth, thirteenth
  } = getFlexibleLengthUnities(length);
  return `${code}${third}${fourth}${fifth}${sixth}${seventh}${eighth}${ninth}${tenth}${eleventh}${twelfth}${thirteenth}`;
}

export {
  getCodePerRecipe,
  getCodeLetterForPdf,
  getCodesWithMessage,
  getCodeForFlexibleFeatures,
  getConnectionDiameterByCode,
  getProductCode,
};
